import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import type { ProfileMenuUser } from './profile-menu.component';

/**
 * 스위처 메뉴에 한 줄로 렌더되는 단체 항목 — 템플릿이 실제로 쓰는 필드만 담는다.
 *
 * `@sbe-spinoff-client/app-shell`은 SpinoffPlatform에 의존하지 않으므로(레이어 역전 금지)
 * 생성된 API 모델(`OrgSummaryDto`)을 import하지 않는다. 소비하는 쪽(예:
 * `SpinoffOrgSwitcherComponent`)은 자신의 DTO를 그대로 넘기면 구조적으로 이 타입에 대입되고,
 * `orgSelected`로 되돌아오는 객체도 넘긴 원본 참조 그대로라 자기 DTO로 다시 좁혀 쓸 수 있다.
 */
export interface OrgSwitcherOrg {
  readonly id: string;
  readonly name: string;
  readonly role?: string | null;
  /** 이 사용자가 이 org 안에서 소속된 team(반) 목록 — `teamsEnabled`가 true일 때만 렌더된다. */
  readonly teams?: readonly { readonly teamId: string; readonly teamName: string }[];
}

/** `teamSelected`로 방출되는 값 — 어느 단체의 어느 team인지. */
export interface OrgSwitcherTeamSelection {
  readonly org: OrgSwitcherOrg;
  readonly teamId: string;
  readonly teamName: string;
}

/**
 * 개인/단체 컨텍스트 전환 드롭다운 버튼("컨텍스트 스위처") — 툴바 좌측(로고 옆)에 배치한다.
 *
 * 인증·단체 스토어에 의존하지 않는 순수 프레젠테이션 컴포넌트다. input으로 받은 값만 그리고
 * 클릭은 output으로 흘려보내므로, SpinoffPlatform lib를 쓰는 앱이든 자체 인증을 쓰는 앱이든
 * (예: schoolbell-e/web) 같은 UI를 재사용할 수 있다 — `ProfileMenuComponent`(같은 lib)를
 * 순수 컴포넌트로 뽑아낸 것과 동일한 패턴이다. 데이터 로딩·재시도·다이얼로그·스낵바는 전부
 * 감싸는 쪽(wrapper)의 책임이다.
 *
 * - `orgs`: 메뉴에 나열할 단체 목록.
 * - `activeOrgId`: 현재 활성 단체 ID (null = 개인 모드) — 체크 표시 기준.
 * - `personalContextEnabled`: 메뉴에 "개인" 항목 자체를 노출할지 여부(기본 true). 모든 화면이
 *   org 스코프라 개인 모드가 아무 콘텐츠도 없는 앱(예: sbe-schedule)은 false로 꺼 혼란스러운
 *   항목을 감춘다 — 앱별 opt-out, 기본은 하위호환 유지.
 * - `currentUser`: 트리거/개인 항목 라벨·이니셜 폴백용(이름 → 이메일 → 전화번호).
 * - `invitationsEnabled`: 메뉴에 "받은 초대" 항목 자체를 노출할지 여부(기본 true). org 초대
 *   기능이 아직 없는 앱(예: schoolbell-e/web)은 false로 꺼 아무 동작도 안 하는 항목을
 *   숨긴다 — `personalContextEnabled`와 동일한 앱별 opt-out 패턴.
 * - `pendingInvitationsCount`: "받은 초대" 항목의 배지 숫자(0이면 배지 숨김) — `invitationsEnabled`가
 *   false면 무시된다.
 * - `teamsEnabled`: 단체 항목 아래에 이 사용자의 소속 team(반) 이름을 읽기 전용으로 표시할지
 *   여부(기본 false — **opt-in**, 2026-08-18). team 데이터가 아직 준비 안 된 앱에서 빈 줄이나
 *   `undefined` 노출 없이 하위호환을 유지하기 위해 다른 opt-*(personalContextEnabled 등)와
 *   반대로 기본값을 false로 둔다 — 앱이 명시적으로 켜야 보인다. 전환 동작은 없다(클릭해도
 *   컨텍스트가 안 바뀜) — 지금 org 소속 team을 확인하는 용도.
 * - `teamSelectionEnabled`: `teamsEnabled`인 단체 아래 team을 읽기 전용 배지 대신 **클릭 가능한
 *   메뉴 항목**으로 렌더하고 `teamSelected`를 방출할지 여부(기본 false — **opt-in**). 팀 화면으로
 *   들어가는 진입점을 스위처로 두는 앱(schoolbell-e/web)만 켠다. 꺼져 있으면 기존 배지 동작 그대로다.
 * - `orgSelected` / `personalSelected`: 항목 클릭 시 방출.
 * - `teamSelected`: `teamSelectionEnabled`일 때 team 항목 클릭 시 방출.
 * - `invitationsRequested`: "받은 초대" 클릭 시 방출.
 * - `createOrgRequested`: "단체 만들기" 클릭 시 방출 — 다이얼로그를 여는 것은 이 컴포넌트가
 *   아니라 부모의 책임이다.
 * - `menuOpened`: 컨텍스트 메뉴가 열릴 때 방출 — 부모가 곧 쓸 지연 청크(예: 단체 만들기 다이얼로그)를
 *   미리 받아 두는 용도다.
 */
@Component({
  selector: 'sbe-org-switcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatBadgeModule, MatButtonModule, MatIconModule, MatMenuModule],
  template: `
    <!-- 트리거 버튼 -->
    <button mat-button type="button" [matMenuTriggerFor]="contextMenu" (menuOpened)="menuOpened.emit()" class="os__trigger">
      <span class="os__trigger-inner">
        <span class="os__avatar">{{ contextInitial() }}</span>
        <span class="os__label">{{ contextLabel() }}</span>
        <mat-icon class="os__arrow">arrow_drop_down</mat-icon>
      </span>
    </button>

    <mat-menu #contextMenu="matMenu">
      <!-- 개인 -->
      @if (personalContextEnabled()) {
        <button mat-menu-item type="button" (click)="personalSelected.emit()">
          <span matMenuItemIcon class="os__item-icon">
            <span class="os__item-avatar">{{ personalInitial() }}</span>
          </span>
          <span>{{ personalLabel() }}</span>
          <span class="os__role-badge">개인</span>
          <mat-icon class="os__check" [class.os__check--hidden]="!!activeOrgId()">check</mat-icon>
        </button>
      }

      <!-- 단체 목록 -->
      @for (org of orgs(); track org.id) {
        <button mat-menu-item type="button" (click)="orgSelected.emit(org)">
          <span matMenuItemIcon class="os__item-icon">
            <span class="os__item-avatar">{{ org.name.charAt(0).toUpperCase() }}</span>
          </span>
          <span>{{ org.name }}</span>
          <!-- team에만 배치되고 org 직속 role이 없으면 null이다(2026-07-11) — 배지를 숨긴다. -->
          @if (org.role) {
            <span class="os__role-badge">{{ org.role }}</span>
          }
          <!-- teamName이 빈 문자열인 team만 있으면 dedup 후에도 teamNames가 ""일 수 있다 —
               'as names'로 한 번 더 걸러 빈 배지(줄바꿈만 있는 빈 span)를 렌더하지 않는다. -->
          @if (teamsEnabled() && !teamSelectionEnabled() && org.teams?.length) {
            @if (teamNames(org); as names) {
              <span class="os__team-badge">{{ names }}</span>
            }
          }
          <mat-icon class="os__check" [class.os__check--hidden]="activeOrgId() !== org.id">check</mat-icon>
        </button>
        <!-- 팀 진입(opt-in) — 단체 항목 바로 아래 들여쓴 항목. 같은 teamId는 한 번만 그린다. -->
        @if (teamsEnabled() && teamSelectionEnabled()) {
          @for (team of uniqueTeams(org); track team.teamId) {
            <button mat-menu-item type="button" class="os__team-item" (click)="teamSelected.emit({ org, teamId: team.teamId, teamName: team.teamName })">
              <span>{{ team.teamName }}</span>
            </button>
          }
        }
      }

      <!-- 받은 초대 -->
      @if (invitationsEnabled()) {
        <!--
          배지는 aria-hidden 인 mat-icon 이 아니라 감싸는 span 에 붙인다 — mat-icon 에 붙이면 Material 이 'matBadge on an
          aria-hidden mat-icon' 경고를 내고 스크린리더가 개수를 읽지 못한다. 개수는 버튼 aria-label 로 전달하고(알림 벨과 같은
          방식), 0건이면 label 없이 본문 "받은 초대"가 이름이 된다. 아이콘 margin 은 span 으로 옮겨 배지 위치(아이콘 오른쪽 위)를 유지한다.
        -->
        <button
          mat-menu-item
          type="button"
          [attr.aria-label]="pendingInvitationsCount() > 0 ? '받은 초대 — 미수락 ' + pendingInvitationsCount() + '건' : null"
          (click)="invitationsRequested.emit()"
        >
          <span
            matMenuItemIcon
            class="os__invite-icon"
            [matBadge]="pendingInvitationsCount()"
            [matBadgeHidden]="pendingInvitationsCount() === 0"
            matBadgeColor="warn"
            matBadgeSize="small"
          >
            <mat-icon>mail_outline</mat-icon>
          </span>
          받은 초대
        </button>
      }

      <!-- 단체 만들기 -->
      <button mat-menu-item type="button" (click)="createOrgRequested.emit()">
        <mat-icon matMenuItemIcon>add_circle_outline</mat-icon>
        단체 만들기
      </button>
    </mat-menu>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      /* 트리거 버튼 */
      .os__trigger {
        border-radius: 9999px;
        padding-left: 4px;
        padding-right: 8px;
        min-width: 0;
      }
      .os__trigger-inner {
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .os__avatar {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        color: inherit;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.75rem;
        flex-shrink: 0;
      }
      .os__label {
        font-size: 0.875rem;
        font-weight: 500;
        max-width: 144px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .os__arrow {
        font-size: 20px;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        margin-left: -2px;
      }

      /* 메뉴 내부 — 체크(.os__check)는 항목 콘텐츠(mat-mdc-menu-item-text, Angular Material이
         비-아이콘 프로젝션 콘텐츠를 자동으로 묶는 block 래퍼)의 흐름 밖으로 빼서 항목 박스
         자체의 우측 끝에 고정한다. 그 래퍼는 이 컴포넌트 뷰가 만든 엘리먼트가 아니라(Angular
         Material 내부 템플릿 산출물) 컴포넌트 스코프 스타일로 레이아웃을 못 바꾸므로,
         .mat-mdc-menu-item(버튼 자신 — 이 컴포넌트 템플릿에서 직접 authoring해 스코프 셀렉터가
         정상 적용됨)을 기준으로 절대 위치를 잡는 방식으로 그 래퍼를 우회한다. */
      /* !important 필요: Angular Material 내부 구조 CSS(.mat-mdc-menu-item류)가 이 컴포넌트
         스코프 스타일과 specificity가 같은 선택자로 padding-right/크기를 다시 정의하면서,
         소스 순서상 나중에 주입돼(같은 이유로 다이얼로그 padding-top도 !important가
         필요했다) 동일 specificity로는 못 이긴다. */
      .mat-mdc-menu-item {
        position: relative;
        padding-right: 34px !important;
      }
      /* "받은 초대"/"단체 만들기"의 실제 mat-icon은 Material 내부 규칙
         (.mat-mdc-menu-item .mat-icon { margin-right: var(--mat-menu-item-spacing) })이
         .mat-icon 클래스를 기준으로 매칭돼 자동으로 오른쪽 여백이 붙는다. 이 아바타 슬롯은
         matMenuItemIcon만 붙인 커스텀 span이라 .mat-icon 클래스가 없어 그 규칙에 안 걸린다 —
         같은 토큰을 그대로 참조해 맞춘다. 슬롯 너비도 실제 mat-icon 크기 토큰으로 고정해
         (아바타 28px 자체는 그대로 두고 슬롯 안에서 중앙 정렬) mat-icon 항목과 텍스트 시작
         위치를 맞춘다 — 아바타가 24px 슬롯보다 커서 좌우로 2px씩 살짝 넘치지만, 어차피
         아바타는 원형이라 눈에 띄지 않는다. */
      .os__item-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--mat-menu-item-icon-size, 24px);
        margin-right: var(--mat-menu-item-spacing, 12px);
      }
      /* margin-right:0 !important 필요: 이 아이콘도 진짜 <mat-icon>이라 위와 같은 내부 규칙의
         margin-right(12px)이 그대로 걸린다. absolute 포지셔닝에서는 이 margin이 right 오프셋에
         그대로 더해져(12+12=24px) 의도한 위치보다 텍스트 쪽으로 더 밀고 들어가 겹쳐 보였다. */
      .os__check {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        font-size: 16px !important;
        width: 16px !important;
        height: 16px !important;
        margin-right: 0 !important;
        flex-shrink: 0;
      }
      .os__check--hidden {
        visibility: hidden;
      }
      /* 받은 초대 아이콘 래퍼 — 메뉴 항목 아이콘 규칙(.mat-mdc-menu-item .mat-icon 의 margin)을 래퍼로 옮겨 배지 기준 박스가 아이콘(24×24)과 같게 한다. */
      .os__invite-icon {
        display: inline-flex;
        flex-shrink: 0;
        margin-inline-end: var(--mat-menu-item-spacing, 12px);
      }
      .os__invite-icon .mat-icon {
        margin: 0;
      }
      .os__item-avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: var(--mat-sys-secondary-container);
        color: var(--mat-sys-on-secondary-container);
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 0.75rem;
        flex-shrink: 0;
        text-transform: uppercase;
      }
      /* 일반 inline span이면 이름과 한 줄에 붙는다 — block으로 줄바꿈해 "이름 / 역할" 2줄
         구성을 유지한다(체크와 묶여 있던 이전 구조와 달리, 체크는 위에서 absolute로 완전히
         분리됨). team 배지(.os__team-badge)도 같은 레이아웃 규칙을 공유하되, role과 의미가
         달라 스타일을 독립적으로 바꿀 수 있도록 별도 클래스로 둔다(2026-08-18 코드 리뷰). */
      /* 팀 항목 — 단체 항목 아래 한 단계 들여쓴다. */
      .os__team-item {
        padding-left: 56px;
      }

      .os__role-badge,
      .os__team-badge {
        display: block;
        font-size: 0.7rem;
        opacity: 0.5;
      }
    `,
  ],
})
export class OrgSwitcherComponent {
  readonly orgs = input<readonly OrgSwitcherOrg[]>([]);
  readonly activeOrgId = input<string | null>(null);
  readonly personalContextEnabled = input<boolean>(true);
  readonly currentUser = input<ProfileMenuUser | null>(null);
  readonly invitationsEnabled = input<boolean>(true);
  readonly pendingInvitationsCount = input<number>(0);
  readonly teamsEnabled = input<boolean>(false);
  readonly teamSelectionEnabled = input<boolean>(false);

  readonly orgSelected = output<OrgSwitcherOrg>();
  readonly teamSelected = output<OrgSwitcherTeamSelection>();
  readonly personalSelected = output<void>();
  readonly invitationsRequested = output<void>();
  readonly createOrgRequested = output<void>();
  readonly menuOpened = output<void>();

  protected readonly contextLabel = computed(() => {
    const activeId = this.activeOrgId();
    if (activeId) {
      const org = this.orgs().find((o) => o.id === activeId);
      if (org) return org.name;
    }
    // personalContextEnabled=false인 앱(schoolbell-e/web, sbe-schedule)은 개인 컨텍스트
    // 자체가 없다 — 활성 단체가 없을 때 currentUser 기반 "개인" 라벨로 폴백하면 존재하지
    // 않는 컨텍스트를 트리거에 노출하는 회귀가 된다(코드 리뷰 지적, PR #2103 —
    // schoolbell-e/web의 구 로컬 구현은 이 상태에서 "단체 선택"을 보여줬었다).
    if (!this.personalContextEnabled()) {
      return '단체 선택';
    }
    const u = this.currentUser();
    return u?.name ?? u?.email ?? u?.phone ?? '개인';
  });

  protected readonly contextInitial = computed(() => (this.contextLabel().charAt(0) || '?').toUpperCase());

  protected readonly personalLabel = computed(() => {
    const u = this.currentUser();
    return u?.name ?? u?.email ?? u?.phone ?? '–';
  });

  // profileMenuInitial()과 폴백 순서(이름→이메일→전화)는 같지만 그 함수의 빈 문자열 폴백('?')을
  // 그대로 쓰면 안 된다 — personalLabel()이 이미 '–' placeholder를 반환해 두므로, 여기서
  // profileMenuInitial을 재사용하면 name이 빈 문자열('')인 엣지 케이스에서 원래는 빈 이니셜을
  // 보여주던 것이 '?'로 바뀌는 미검증 동작 변경이 생긴다(코드 리뷰 지적, PR 생성 전 자체 리뷰).
  // personalLabel 기준으로 직접 판정해 원본 동작을 그대로 유지한다.
  protected readonly personalInitial = computed(() => {
    const label = this.personalLabel();
    return label === '–' ? '?' : label.charAt(0).toUpperCase();
  });

  /**
   * teamId로 dedup하고 공백뿐인 이름은 뺀 team 목록 — 배지(`teamNames`)와 클릭 항목이 공유한다.
   * schoolbell-e의 `MyOrgTeamContextDto`는 team "구조"가 아니라 그 team 안의 자리(seat)마다 한 행을
   * 반환해서(예: 같은 반에 형제가 둘이면 부모 쪽엔 같은 teamId가 두 번 온다), dedup 없이 그대로
   * 쓰면 같은 팀이 중복 노출된다(2026-08-18 코드 리뷰로 발견). 공백뿐인 teamName은 join에서 빈
   * 항목이 구분자만 남기므로 trim한 값만 담는다. teamName은 타입상 필수지만 이 컴포넌트가 받는
   * org.teams는 결국 외부 API 응답을 거쳐 오므로 null/undefined 방어로 옵셔널 체이닝을 쓴다.
   */
  protected uniqueTeams(org: OrgSwitcherOrg): { teamId: string; teamName: string }[] {
    const seen = new Set<string>();
    const teams: { teamId: string; teamName: string }[] = [];
    for (const t of org.teams ?? []) {
      if (seen.has(t.teamId)) continue;
      seen.add(t.teamId);
      const trimmed = t.teamName?.trim();
      if (trimmed) teams.push({ teamId: t.teamId, teamName: trimmed });
    }
    return teams;
  }

  /** 여러 team이면 쉼표로 이어 한 줄에 담는다. */
  protected teamNames(org: OrgSwitcherOrg): string {
    return this.uniqueTeams(org)
      .map((t) => t.teamName)
      .join(', ');
  }
}
